import { NavLink, Outlet, useLocation } from "react-router-dom";
import { setToken } from "@/lib/api";
import {
  LayoutDashboard, Building2, Users, Package, FileStack, ShieldCheck, Award, Sparkles, LogOut, CalendarClock,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Overview", icon: LayoutDashboard, end: true, testid: "nav-overview" },
  { to: "/bids", label: "Bids & Documents", icon: FileStack, testid: "nav-bids" },
  { to: "/tracker", label: "Bid Tracker", icon: CalendarClock, testid: "nav-tracker" },
  { to: "/builder", label: "AI Document Builder", icon: Sparkles, testid: "nav-builder" },
  { to: "/company", label: "Company Letterhead", icon: Building2, testid: "nav-company" },
  { to: "/buyers", label: "Buyers Library", icon: Users, testid: "nav-buyers" },
  { to: "/products", label: "Products Library", icon: Package, testid: "nav-products" },
  { to: "/certificates", label: "Certificate Library", icon: Award, testid: "nav-certificates" },
];

export default function AppShell() {
  const location = useLocation();
  const isWorkspace = /^\/bids\/[^/]+$/.test(location.pathname);

  return (
    <div className="min-h-screen bg-background text-foreground flex">
      {/* Sidebar */}
      <aside className="hidden lg:flex w-72 shrink-0 flex-col border-r border-border bg-white/60 backdrop-blur-sm sticky top-0 h-screen">
        <div className="px-6 py-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-slate-900 flex items-center justify-center">
              <ShieldCheck className="h-5 w-5 text-emerald-400" />
            </div>
            <div>
              <div className="font-head font-extrabold tracking-tight text-lg leading-none">BidDesk</div>
              <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-emerald-700 mt-1">GeM Docs</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testid}
              className={({ isActive }) =>
                `group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-150 ${
                  (isActive || (item.to === "/bids" && isWorkspace))
                    ? "bg-slate-900 text-white"
                    : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                }`
              }
            >
              <item.icon className="h-4 w-4" />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="px-3 py-4 border-t border-border">
          <button
            onClick={() => { setToken(null); window.location.href = "/login"; }}
            data-testid="logout-btn"
            className="w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-slate-600 hover:bg-red-50 hover:text-red-600 transition-colors"
          >
            <LogOut className="h-4 w-4" /> Log out
          </button>
          <div className="px-3 py-3 text-xs text-slate-500">
            Focus on business.<br />We handle the paperwork.
          </div>
        </div>
      </aside>

      {/* Mobile top nav */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-30 bg-white/90 backdrop-blur border-b border-border">
        <div className="flex items-center gap-1 overflow-x-auto px-2 py-2 scrollbar-thin">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `flex items-center gap-2 whitespace-nowrap rounded-lg px-3 py-2 text-xs font-medium ${
                  isActive ? "bg-slate-900 text-white" : "text-slate-600"
                }`
              }
            >
              <item.icon className="h-4 w-4" />
              {item.label}
            </NavLink>
          ))}
        </div>
      </div>

      <main className="flex-1 min-w-0 pt-16 lg:pt-0">
        <Outlet />
      </main>
    </div>
  );
}
